class SelectCustom extends HTMLElement {
  connectedCallback() {
    this._select = this.querySelector('select');
    this._details = this.querySelector('details');
    this._summary = this._details ? this._details.querySelector('summary span') : null;
    this._labels = Array.from(this.querySelectorAll('label[data-input-value]'));

    // "Other" input elements
    this._otherWrapper = this.querySelector('.form-field--other-input');
    this._otherInput = this._otherWrapper ? this._otherWrapper.querySelector('input') : null;
    this._otherUndoBtn = this._otherWrapper
      ? this._otherWrapper.querySelector('.form-field--other-undo')
      : null;

    if (!this._select || !this._details) return;

    this._onLabelClick = this._onLabelClick.bind(this);
    this._onSelectChange = this._onSelectChange.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onUndoClick = this._onUndoClick.bind(this);

    this._labels.forEach(function (label) {
      label.addEventListener('click', this._onLabelClick);
    }, this);

    // Keep in sync if the native select is changed some other way
    // (e.g. browser autofill, programmatic value assignment elsewhere)
    this._select.addEventListener('change', this._onSelectChange);

    // Close the dropdown when clicking outside of it
    document.addEventListener('click', this._onDocumentClick);

    // Close on Escape
    this._details.addEventListener('keydown', this._onKeydown);

    if (this._otherUndoBtn) {
      this._otherUndoBtn.addEventListener('click', this._onUndoClick);
    }

    // Reflect whatever the select's initial value is (e.g. reloaded form, default option)
    this._syncFromSelectValue(this._select.value, { silent: true });

    // Evaluate conditional fields once on load, so a pre-filled/back-navigated
    // form shows the right fields immediately, not just after the next change.
    this._updateConditionalFields();
  }

  disconnectedCallback() {
    this._labels.forEach(function (label) {
      label.removeEventListener('click', this._onLabelClick);
    }, this);

    if (this._select) {
      this._select.removeEventListener('change', this._onSelectChange);
    }
    if (this._details) {
      this._details.removeEventListener('keydown', this._onKeydown);
    }
    if (this._otherUndoBtn) {
      this._otherUndoBtn.removeEventListener('click', this._onUndoClick);
    }
    document.removeEventListener('click', this._onDocumentClick);
  }

  _onLabelClick(event) {
    const label = event.currentTarget;
    const value = label.dataset.inputValue;

    this._select.value = value;
    this._select.dispatchEvent(new Event('change', { bubbles: true }));

    this._syncFromSelectValue(value);
    this._closeDetails();
    this._updateConditionalFields();
  }

  _onSelectChange() {
    this._syncFromSelectValue(this._select.value);
    this._updateConditionalFields();
  }

  _onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this._closeDetails();
    }
  }

  _onKeydown(event) {
    if (event.key === 'Escape') {
      this._closeDetails();
      this._details.querySelector('summary').focus();
    }
  }

  _closeDetails() {
    if (this._details.hasAttribute('open')) {
      this._details.removeAttribute('open');
    }
  }

  _syncFromSelectValue(value, options) {
    options = options || {};

    // Update summary label text
    const matchedOption = this._select.querySelector(
      'option[value="' + CSS.escape(value) + '"]'
    );
    const displayText = matchedOption ? matchedOption.textContent : '';

    if (this._summary && displayText) {
      this._summary.textContent = displayText;
    }

    // Toggle a "selected" state class on the matching label, clear it on the rest
    this._labels.forEach(function (label) {
      const isMatch = label.dataset.inputValue === value;
      label.classList.toggle('is-selected', isMatch);
    });

    // Show/hide the free-text "other" field based on the selected value
    this._toggleOtherMode(this._isOtherTrigger(value));

    // Dispatch a bubbling custom event so other components (badges,
    // dependent fields, etc.) can react without coupling to internals
    if (!options.silent) {
      this.dispatchEvent(
        new CustomEvent('select-custom:change', {
          bubbles: true,
          detail: {
            name: this._select.name,
            value: value,
            label: displayText
          }
        })
      );
    }
  }

  // ---------------------------------------------------------------------
  // "Other / Undecided" free-text field
  // ---------------------------------------------------------------------

  // Matches "Other", "Other/Not Applicable", "Undecided",
  // "Undecided / Not applicable", etc. — any option whose value starts
  // with "other" or "undecided" (case-insensitive) triggers the free-text field.
  _isOtherTrigger(value) {
    if (!value) return false;
    return /^(other|undecided)/i.test(value.trim());
  }

  _toggleOtherMode(isOther) {
    if (!this._otherWrapper || !this._otherInput) return;

    if (isOther) {
      this._showOtherMode();
    } else {
      this._hideOtherMode();
    }
  }

  _showOtherMode() {
    // Hide + disable the native select and the custom dropdown
    this._select.disabled = true;
    this._select.classList.add('m:hidden');

    this._closeDetails();
    this._details.classList.add('m:hidden');
    this._details.setAttribute('aria-hidden', 'true');

    // Show + enable the free-text input
    this._otherWrapper.classList.remove('m:hidden');
    this._otherInput.disabled = false;
    this._otherInput.required = true;
    this._otherInput.focus();
  }

  _hideOtherMode() {
    // Hide + disable + clear the free-text input
    this._otherWrapper.classList.add('m:hidden');
    this._otherInput.disabled = true;
    this._otherInput.required = false;
    this._otherInput.value = '';

    // Restore the native select and the custom dropdown
    this._select.disabled = false;
    this._select.classList.remove('m:hidden');
    this._details.classList.remove('m:hidden');
    this._details.removeAttribute('aria-hidden');
  }

  _onUndoClick(event) {
    event.preventDefault();

    // Restore the original (blank/default) select state
    this._select.value = '';
    this._select.dispatchEvent(new Event('change', { bubbles: true }));

    this._syncFromSelectValue('');
    this._updateConditionalFields();

    const summaryEl = this._details.querySelector('summary');
    if (summaryEl) summaryEl.focus();
  }

  // ---------------------------------------------------------------------
  // Conditional field visibility (data-conditions)
  // ---------------------------------------------------------------------

  // Re-evaluate every [data-conditions] field in this select's form.
  // Loops (bounded) so that a field revealed/hidden by this pass can
  // itself unblock or invalidate a field that depends on it.
  _updateConditionalFields() {
    const form = this._select.closest('form');
    if (!form) return;

    let didChange = true;
    let iterations = 0;
    const MAX_ITERATIONS = 5;

    while (didChange && iterations < MAX_ITERATIONS) {
      didChange = false;
      iterations++;

      const conditionalFields = form.querySelectorAll('[data-conditions]');
      conditionalFields.forEach(function (fieldEl) {
        const groups = SelectCustom._parseConditions(fieldEl.dataset.conditions || '');
        const shouldShow = SelectCustom._matchesConditions(form, groups);
        const isCurrentlyHidden = fieldEl.classList.contains('m:hidden');

        if (shouldShow === isCurrentlyHidden) {
          SelectCustom._setFieldVisibility(fieldEl, shouldShow);
          didChange = true;
        }
      });
    }
  }

  // "Jewelry type = A,\nJewelry type = B" -> { "Jewelry type": ["A", "B"] }
  // Multiple values for the *same* field are OR'd together.
  // Multiple *different* fields (if ever used) are AND'd together.
  static _parseConditions(raw) {
    const groups = {};
    raw.split(',').forEach(function (part) {
      const eqIndex = part.indexOf('=');
      if (eqIndex === -1) return;

      const field = part.slice(0, eqIndex).trim();
      const value = part.slice(eqIndex + 1).trim();
      if (!field || !value) return;

      if (!groups[field]) groups[field] = [];
      groups[field].push(value);
    });
    return groups;
  }

  static _matchesConditions(form, conditionGroups) {
    const fieldNames = Object.keys(conditionGroups);
    if (fieldNames.length === 0) return true;

    return fieldNames.every(function (fieldName) {
      const select = form.querySelector('select[name="contact[' + fieldName + ']"]');
      if (!select) return false;
      return conditionGroups[fieldName].indexOf(select.value) !== -1;
    });
  }

  static _setFieldVisibility(fieldEl, shouldShow) {
    const select = fieldEl.querySelector('select');
    const wasHidden = fieldEl.classList.contains('m:hidden');

    if (shouldShow) {
      fieldEl.classList.remove('m:hidden');
      if (select) select.disabled = false;
    } else {
      // Clear a stale value before hiding so a hidden field can never be submitted
      if (!wasHidden && select) {
        select.value = '';
      }
      fieldEl.classList.add('m:hidden');
      if (select) select.disabled = true;
    }
  }
}

customElements.define('select-custom', SelectCustom);